import { useState } from "react";
import { motion } from "framer-motion";
import { MENU, CATEGORIES, SPOTLIGHT } from "./data";
import { Reveal, Eyebrow, EASE } from "./Reveal";

export default function MenuSection() {
  const [cat, setCat] = useState("All");
  const dishes = cat === "All" ? MENU : MENU.filter((d) => d.cat === cat);
  const spots = SPOTLIGHT.map((s) => ({
    ...MENU.find((d) => d.slug === s.slug),
    img: s.img,
  }));

  return (
    <section id="menu" className="relative py-28 md:py-40" data-testid="menu-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <Reveal className="mb-12 md:mb-16">
          <Eyebrow className="mb-4">The menu — straight off the coals</Eyebrow>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h2 className="font-display font-semibold text-4xl md:text-6xl leading-tight">
              Order the fire.
            </h2>
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-dim/80 max-w-xs leading-loose">
              Prices as shared on our Instagram &amp; Google reviews — ask at
              the counter for today&apos;s full card.
            </p>
          </div>
        </Reveal>

        {/* Category tabs */}
        <Reveal delay={0.1} className="mb-12 md:mb-16">
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => {
              const active = cat === c;
              return (
                <button
                  key={c}
                  data-testid={`menu-tab-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  onClick={() => setCat(c)}
                  className={`relative rounded-full px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.18em] transition-colors duration-300 ${
                    active ? "text-charcoal" : "text-dim hover:text-bone border border-white/15 hover:border-gold/60"
                  }`}
                >
                  {active && (
                    <motion.span
                      layoutId="tab-pill"
                      className="absolute inset-0 rounded-full bg-ember"
                      transition={{ duration: 0.5, ease: EASE }}
                    />
                  )}
                  <span className="relative z-10">{c}</span>
                </button>
              );
            })}
          </div>
        </Reveal>

        {/* Spotlight cards — All view */}
        {cat === "All" && (
          <div className="grid md:grid-cols-3 gap-6 mb-16 md:mb-20">
            {spots.map((s, i) => (
              <Reveal key={s.slug} delay={i * 0.1}>
                <div
                  data-testid={`menu-card-${s.slug}`}
                  className="group relative overflow-hidden rounded-2xl border border-white/10 bg-cardc aspect-[4/5]"
                >
                  <img
                    src={s.img}
                    alt={s.name}
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover brightness-[0.8] saturate-[0.9] transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/25 to-transparent" />
                  <span className="chip absolute top-4 left-4">{s.tag}</span>
                  <div className="absolute bottom-0 inset-x-0 p-6">
                    <div className="flex items-baseline justify-between gap-3">
                      <h4 className="font-display font-semibold text-3xl">
                        {s.name}
                      </h4>
                      <span className="font-mono text-ember text-sm whitespace-nowrap">
                        {s.price || "Ask"}
                      </span>
                    </div>
                    <p className="text-dim text-sm mt-2 leading-relaxed">
                      {s.desc}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        )}

        {/* Dish rows */}
        <motion.div
          key={cat}
          initial="hidden"
          animate="show"
          variants={{
            hidden: {},
            show: { transition: { staggerChildren: 0.05 } },
          }}
        >
          {dishes.map((d) => (
            <motion.div
              key={d.slug}
              data-testid={`menu-dish-${d.slug}`}
              variants={{
                hidden: { opacity: 0, y: 18 },
                show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
              }}
              className="group border-t border-white/10 py-7 md:py-8 px-1 transition-colors duration-300 hover:bg-white/[0.02]"
            >
              <div className="flex items-baseline gap-3 flex-wrap">
                <h4 className="font-display font-semibold text-2xl md:text-3xl transition-colors duration-300 group-hover:text-ember">
                  {d.name}
                </h4>
                <span className="chip">{d.tag}</span>
                <span className="leader hidden sm:block" />
                {d.price ? (
                  <span className="font-mono text-ember text-lg ml-auto sm:ml-0">
                    {d.price}
                  </span>
                ) : (
                  <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-dim/70 border border-white/15 rounded-full px-3 py-1.5 ml-auto sm:ml-0">
                    Ask at counter
                  </span>
                )}
              </div>
              <p className="text-dim text-sm md:text-base mt-2 max-w-2xl leading-relaxed">
                {d.desc}
              </p>
            </motion.div>
          ))}
          <div className="border-t border-white/10" />
        </motion.div>
      </div>
    </section>
  );
}
